import { DateTime } from "luxon";
import Chevron from "../../../assets/chevron-right.svg?react";
import { formatTime } from "../../../utils/dateUtils";
import { useGetVisitDetails, VisitStatus } from "../../../hooks/visitStatus";
import classNames from "classnames";
import { VisitPill } from "./VisitPill";
import { useCareContext } from "../../../providers";
import {
  CareHome,
  CareRecipient,
  PartialVisitorFieldsFragment,
  TimeFormat,
} from "../../../api/generated/graphql";
import { Avatar } from "@frontend/lyng/avatar";
import { CareGiver } from "@frontend/lyng/api/generated/graphql";
import { Location } from "@frontend/lyng/assets/icons/16/filled";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { match } from "ts-pattern";
import { useMemo } from "react";
import { parseAddress } from "../../../utils/addressUtils";
import { Label, Paragraph } from "@frontend/lyng/typography";
import { useTranslate } from "@tolgee/react";

type ShiftLabel = {
  text: string;
  variant?: "primary" | "secondary" | "tertiary" | "critical";
};

type Props = {
  from: DateTime;
  to: DateTime;
  subject?:
    | Omit<CareHome, "settings" | "region" | "activities">
    | CareRecipient;
  caregiver?: Pick<CareGiver, "id" | "firstName" | "lastName">;
  clockIn: DateTime | null;
  clockOut: DateTime | null;
  readOnly?: boolean;
  cancelledAt?: string | null;
  importantInformation?: string | null;
  visitors?: PartialVisitorFieldsFragment[];
  labels?: ShiftLabel[];
};

type Gradient = {
  [key in VisitStatus]?: string | undefined;
};

const getBackground = (status: VisitStatus, readOnly?: boolean) =>
  match({ status, readOnly })
    .with({ readOnly: true }, () => "bg-gray-80")
    .with(
      { status: VisitStatus.started },
      () => "bg-visit-card-gradient-started border border-primary-400",
    )
    .with(
      { status: VisitStatus.over },
      () => "bg-visit-card-gradient-started border border-primary-400",
    )
    .with(
      { status: VisitStatus.cancelled },
      () => "bg-visit-card-gradient-cancelled",
    )
    .with({ status: VisitStatus.pending }, () => "bg-primary-10")
    .with({ status: VisitStatus.done }, () => "bg-secondary-800")
    .otherwise(() => "bg-visit-card-gradient-base");

const getGradient = (status: VisitStatus, readOnly?: boolean) => {
  if (readOnly) return null;

  const gradients: Gradient = {
    [VisitStatus.soon]: "bg-visit-card-gradient-bright",
    [VisitStatus.now]: "bg-visit-card-gradient-bright",
    [VisitStatus.late]: "bg-visit-card-gradient-bright",
    [VisitStatus.away]: "bg-visit-card-gradient-reverse",
    [VisitStatus.over]: "bg-visit-card-gradient-reverse",
  };

  if (!gradients[status]) return null;

  return (
    <div
      className={`absolute inset-0 h-full w-full animate-opacity bg-primary-10 ${gradients[status]}`}
    />
  );
};

const getFontColor = (status: VisitStatus, readOnly?: boolean) => {
  return match({ status, readOnly })
    .with({ readOnly: true }, () => "text-gray-20")
    .with({ status: VisitStatus.away }, () => "text-secondary-90")
    .with({ status: VisitStatus.over }, () => "text-secondary-90")
    .otherwise(() => "text-gray-20");
};

const getImportantInfoColor = (status: VisitStatus) => {
  match(status)
    .with(VisitStatus.done, () => "bg-secondary-800")
    .with(VisitStatus.over, () => "bg-secondary-800")
    .otherwise(() => "bg-primary-80");
};

const getHelpers = (status: VisitStatus, readOnly?: boolean) => ({
  background: getBackground(status, readOnly),
  importantInfocolor: getImportantInfoColor(status),
  gradient: getGradient(status, readOnly),
  color: getFontColor(status, readOnly),
});

export const ShiftCard = ({
  from,
  to,
  subject,
  clockIn,
  clockOut,
  readOnly,
  cancelledAt,
  importantInformation,
  caregiver,
  visitors = [],
}: Props) => {
  const { t } = useTranslate();
  const {
    state: { viewer },
  } = useCareContext();

  const viewerInfoOnVisitor = useMemo(() => {
    return visitors.find((v) => v.id === viewer?.id);
  }, [visitors, viewer]);

  const memoizedClockIn = useMemo(() => {
    return viewerInfoOnVisitor?.clockInTime
      ? DateTime.fromISO(viewerInfoOnVisitor.clockInTime)
      : clockIn;
  }, [viewerInfoOnVisitor?.clockInTime, clockIn]);

  const memoizedClockOut = useMemo(() => {
    return viewerInfoOnVisitor?.clockOutTime
      ? DateTime.fromISO(viewerInfoOnVisitor.clockOutTime)
      : clockOut;
  }, [viewerInfoOnVisitor?.clockOutTime, clockOut]);

  const {
    status,
    detailsText: detailsTextFromHook,
    pillText,
  } = useGetVisitDetails({
    from,
    to,
    clockIn: memoizedClockIn,
    clockOut: memoizedClockOut,
    readOnly,
    cancelledAt,
  });
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  const detailsText = useMemo(() => {
    if (readOnly && !caregiver) {
      const firstLetter = detailsTextFromHook.charAt(0).toUpperCase();
      return firstLetter + detailsTextFromHook.slice(1);
    }
    return detailsTextFromHook;
  }, [readOnly, caregiver, detailsTextFromHook]);

  const isMeridian = useMemo(
    () => viewer?.tenantSettings.timeFormat === TimeFormat.AmPm,
    [viewer?.tenantSettings.timeFormat],
  );

  const time = `${formatTime(from, isMeridian)} - ${formatTime(to, isMeridian)}`;
  const helpers = getHelpers(status, readOnly);
  const address = useMemo(() => {
    if (!subject) return null;
    return parseAddress(subject.address);
  }, [subject]);

  const subjectName = useMemo(
    () =>
      match(subject)
        .with({ __typename: "CareRecipient" }, (sub) => nameOrderFn(sub))
        .with({ __typename: "CareHome" }, (sub) => sub.name)
        .otherwise(() => ""),
    [subject, nameOrderFn],
  );

  const shouldShowCaregiver = useMemo(
    () =>
      readOnly &&
      subject &&
      subject.__typename === "CareRecipient" &&
      caregiver,
    [readOnly, subject, caregiver],
  );

  const visitorsFiltered = useMemo(
    () => visitors.filter((v) => v.id !== viewer?.id),
    [visitors, viewer?.id],
  );

  return (
    <div>
      <div
        className={`relative overflow-hidden rounded-lg ${helpers.background}`}
      >
        {helpers.gradient}
        <div
          className={classNames(
            { "py-3": !importantInformation && !cancelledAt },
            "relative z-10 flex flex-col pt-3 gap-2",
          )}
        >
          <div className="flex flex-col px-4 gap-2">
            <Label size="xs" className="text-gray-10">
              {time}
            </Label>
            <Paragraph size="m">{subjectName}</Paragraph>
            {address && (
              <div className="flex flex-row items-center gap-2 text-greyscale-300 text-sm pr-3">
                <Location />
                <p>{address}</p>
              </div>
            )}
          </div>

          {visitorsFiltered.length > 0 && (
            <div className="flex flex-row items-center gap-[-8px] px-4 mt-1">
              {visitorsFiltered.map((visitor) => (
                <Avatar
                  key={visitor.id}
                  firstName={visitor.firstName ?? ""}
                  lastName={visitor.lastName ?? ""}
                  size="sm"
                  className="-ml-2 first:ml-0 border-2 border-main bg-main"
                  randomizeColor
                />
              ))}
              <span className="ml-2 text-xs text-gray-20 font-medium">
                {t("with")}{" "}
                {visitorsFiltered.map((v) => v.firstName).join(", ")}
              </span>
            </div>
          )}

          <div className="flex w-full flex-row items-center px-4 gap-2">
            {shouldShowCaregiver && (
              <Avatar
                firstName={caregiver?.firstName ?? ""}
                lastName={caregiver?.lastName ?? ""}
              />
            )}
            <div className="flex flex-col">
              {shouldShowCaregiver && (
                <p className="text-xs font-medium text-gray-20">
                  {nameOrderFn({
                    firstName: caregiver?.firstName ?? "",
                    lastName: caregiver?.lastName ?? "",
                  })}
                </p>
              )}
              <p className={`text-xs font-medium ${helpers.color}`}>
                {detailsText}
              </p>
            </div>
            {pillText && (
              <div className="ml-auto">
                <VisitPill text={pillText} status={status} />
              </div>
            )}
            <div className="absolute right-3 top-14 z-10 -translate-y-1/2">
              <Chevron className="text-gray-10" />
            </div>
          </div>

          {importantInformation && !cancelledAt && (
            <div
              className={`flex mt-2 ${helpers.importantInfocolor} w-full px-4 py-3`}
            >
              <p className="text-small text-gray-10">{importantInformation}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
